'use client';

import { type ChangeEvent, type ComponentProps, type ComponentType, type ReactNode } from 'react';
import ReactMaskedInput, { type MaskedInputProps } from 'react-text-mask';

import clsx from 'clsx';

import { type ControlState } from '@/components/common/Control';
import { EyeIcon } from '@/components/common/icons/EyeIcon';
import { useBoolean } from '@/hooks/use-boolean';
import { useCombineRefs } from '@/hooks/use-combine-refs';
import { useElementSize } from '@/hooks/use-element-size';
import { getCssSizeValue } from '@/utils/css';
import { isDefined } from '@/utils/utils';

import styles from './TextInput.module.scss';

export type TextInputProps = Omit<ComponentProps<'input'>, 'prefix'> & {
  variant?: 'default' | 'box';
  prefix?: ReactNode;
  suffix?: ReactNode;
  state?: ControlState;
  mask?: MaskedInputProps['mask'];
  onChange?: (e: ChangeEvent<HTMLInputElement>) => unknown;
};

const MaskedInput = ReactMaskedInput as ComponentType<MaskedInputProps>;

export const TextInput = (props: TextInputProps) => {
  const {
    className,
    ref,
    value,
    type,
    variant = 'default',
    prefix,
    suffix,
    state = 'default',
    mask,
    onChange,
    ...inputProps
  } = props;

  const { value: openedPassword, toggle: toggleOpenedPassword } = useBoolean();

  const inputElementProps = {
    className: styles.input,
    type: type === 'password' && openedPassword ? 'text' : type,
    value: value ?? '',
    ...inputProps,
  };

  const suffixNode =
    suffix ?? (type === 'password' ? <EyeIcon onClick={toggleOpenedPassword} /> : inputProps.required ? '*' : null);

  const { size: prefixSize, ref: prefixRef } = useElementSize();
  const { size: suffixSize, ref: suffixRef } = useElementSize();

  const combinedInputRef = useCombineRefs([ref]);

  return (
    <div
      className={clsx(
        styles.root,
        styles[`variant_${variant}`],
        styles[`state_${state}`],
        {
          [styles.withPrefix]: prefix,
          [styles.withSuffix]: suffixNode,
        },
        className
      )}
      style={{
        '--text-input-prefix-width': getCssSizeValue(prefixSize.width),
        '--text-input-suffix-width': getCssSizeValue(suffixSize.width),
      }}
    >
      {mask ? (
        <MaskedInput
          mask={mask}
          onChange={onChange}
          render={(ref, restProps) => (
            <input
              ref={(input) => {
                combinedInputRef(input);

                if (input) {
                  ref(input);
                }
              }}
              {...restProps}
              {...inputElementProps}
            />
          )}
        />
      ) : (
        <input ref={combinedInputRef} onChange={onChange} {...inputElementProps} />
      )}

      {isDefined(prefix) && (
        <div className={styles.prefix} ref={prefixRef}>
          {prefix}
        </div>
      )}

      {isDefined(suffixNode) && (
        <div className={styles.suffix} ref={suffixRef}>
          {suffixNode}
        </div>
      )}
    </div>
  );
};
